'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import ChannelBadge from '@/components/ui/ChannelBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import PriorityBadge from '@/components/ui/PriorityBadge';
import SentimentBadge, { SentimentType } from '@/components/ui/SentimentBadge';
import SimulateDemoDropdown, { SimulateChannel } from './SimulateDemoDropdown';
import IncomingInteractionNotification, { IncomingChannel } from './IncomingInteractionNotification';
import { getDashboardDate } from '@/lib/demoDate';

const summaryStats = [
  { label: 'Interactions Today', value: '32', delta: '+4 vs yesterday', positive: true, icon: 'ChatBubbleLeftRightIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
  { label: 'Open Cases', value: '7', delta: '2 approaching SLA', positive: false, icon: 'ClipboardDocumentListIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
  { label: 'Pending Follow-Ups', value: '3', delta: 'Due today', positive: false, icon: 'ClockIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
  { label: 'Avg. Handling Time', value: '04:18', delta: '-0:22 vs target', positive: true, icon: 'ChartBarIcon', color: 'text-green-600', bg: 'bg-green-50' },
];

const initialInteractions = [
  { id: 'int-001', channel: 'PHONE' as const, citizen: 'Ahmed Firdaus', topic: 'Ministry of Health Inquiry', queue: 'MyGCC General', elapsed: '00:03', priority: 'High' as const, sentiment: 'Concerned' as SentimentType, status: 'Active' },
  { id: 'int-002', channel: 'WHATSAPP' as const, citizen: 'Nurul Huda', topic: 'Passport Renewal Question', queue: 'Immigration', elapsed: '00:12', priority: 'Medium' as const, sentiment: 'Frustrated' as SentimentType, status: 'Active' },
  { id: 'int-003', channel: 'LIVE CHAT' as const, citizen: 'Daniel Tan', topic: 'STR Eligibility', queue: 'Government Assistance', elapsed: '00:07', priority: 'Medium' as const, sentiment: 'Neutral' as SentimentType, status: 'Active' },
  { id: 'int-004', channel: 'EMAIL' as const, citizen: 'Siti Mariam', topic: 'Application Status', queue: 'General', elapsed: '02:41', priority: 'Low' as const, sentiment: 'Concerned' as SentimentType, status: 'Pending' },
];

const initialCases = [
  { id: 'MYGCC-2026-008420', citizen: 'Kumar Rajan', topic: 'Road Tax Renewal', agency: 'JPJ', status: 'In Progress', priority: 'Medium' as const, sla: '2h 14m', slaStatus: 'risk' },
  { id: 'MYGCC-2026-008418', citizen: 'Nur Aina', topic: 'MyDigital ID Setup', agency: 'JPN', status: 'Awaiting Citizen', priority: 'Low' as const, sla: '4h 30m', slaStatus: 'ok' },
  { id: 'MYGCC-2026-008415', citizen: 'Muhammad Danish', topic: 'KWSP Withdrawal', agency: 'KWSP', status: 'Awaiting Agency', priority: 'High' as const, sla: 'Overdue', slaStatus: 'breach' },
  { id: 'MYGCC-2026-008409', citizen: 'Farah Lim', topic: 'Income Tax Filing', agency: 'LHDNM', status: 'In Progress', priority: 'Medium' as const, sla: '6h 00m', slaStatus: 'ok' },
  { id: 'MYGCC-2026-008401', citizen: 'Azri Hamdan', topic: 'MySejahtera Update', agency: 'KKM', status: 'Resolved', priority: 'Low' as const, sla: 'Resolved', slaStatus: 'resolved' },
];

const initialTasks = [
  { id: 'task-001', label: 'Follow up: Kumar Rajan — Road Tax', due: 'Today, 14:00', urgent: true, type: 'Citizen Follow-Up' },
  { id: 'task-002', label: 'Update case MYGCC-2026-008418 with citizen response', due: 'Today, 16:30', urgent: false, type: 'Case Review' },
  { id: 'task-003', label: 'Review AI draft for MYGCC-2026-008415', due: 'Today, 17:00', urgent: false, type: 'AI Draft Review' },
];

const aiInsights = [
  { text: 'Passport-related enquiries are 18% above normal volume today.', type: 'warning', action: 'View Interactions', href: '/interactions' },
  { text: '27 interactions show repeated citizen frustration — supervisor review recommended.', type: 'warning', action: 'Review Now', href: '/interactions' },
  { text: 'MYGCC-2026-008415 is approaching agency SLA — immediate action required.', type: 'critical', action: 'Open Case', href: '/cases' },
  { text: 'New MySejahtera article reduced average handling time by 12%.', type: 'positive', action: 'View Article', href: '/knowledge-base' },
];

const channelSimData: Record<SimulateChannel, { citizen: string; preview: string; sub?: string; topic?: string }> = {
  voice: { citizen: 'Ahmed Firdaus', preview: '', sub: '+60 12 *** 4581', topic: 'MySejahtera Account Update · MyGCC General' },
  livechat: { citizen: 'Siti Mariam', preview: 'Salam, saya nak tahu dokumen apa yang diperlukan untuk renew passport.', topic: 'Passport Renewal Documentation' },
  whatsapp: { citizen: 'Nurul Huda', preview: 'Saya dah submit permohonan minggu lepas tapi masih belum dapat apa-apa update.', topic: 'Passport Application Status' },
  email: { citizen: 'Daniel Tan', preview: 'Dear MyGCC, saya ingin mendapatkan status permohonan STR saya yang telah dihantar sebelum ini.', topic: 'Status Permohonan STR' },
  x: { citizen: '@amirhakim', preview: 'Hi MyGCC, where can I check my government assistance application status?', topic: 'Application Status Enquiry' },
  facebook: { citizen: 'Farah Nadia', preview: 'Salam, saya nak tahu status permohonan bantuan saya.', topic: 'Facebook Messenger' },
  instagram: { citizen: '@farahnadia', preview: 'Hi MyGCC, boleh bantu saya check status permohonan?', topic: 'Instagram Direct Message' },
};

interface NewCaseFormData {
  citizen: string;
  agency: string;
  topic: string;
  description: string;
  priority: string;
  relatedInteraction: string;
  relatedTicket: string;
  assignment: string;
}

export default function MyWorkspaceContent() {
  const router = useRouter();
  const [interactions, setInteractions] = useState(initialInteractions);
  const [recentCases, setRecentCases] = useState(initialCases);
  const [tasks, setTasks] = useState(initialTasks);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [incomingChannel, setIncomingChannel] = useState<IncomingChannel | null>(null);
  const [showNewCase, setShowNewCase] = useState(false);
  const [newCaseForm, setNewCaseForm] = useState<NewCaseFormData>({
    citizen: '', agency: '', topic: '', description: '', priority: 'Medium',
    relatedInteraction: '', relatedTicket: '', assignment: 'Nur Aisyah',
  });
  const [dashboardDate, setDashboardDate] = useState('');

  // A voice interaction is "genuinely active simulated" only if it was added by the presenter
  // during this session (not just the seeded demo data). We track this with a flag.
  const [hasSimulatedVoice, setHasSimulatedVoice] = useState(false);

  useEffect(() => {
    setDashboardDate(getDashboardDate());
  }, []);

  const handleSimulate = (channel: SimulateChannel) => {
    if (channel === 'voice') {
      // Always reset any existing seeded/mock voice interaction transparently
      // then show the incoming call notification
      setInteractions(prev => prev.filter(i => i.channel !== 'PHONE'));
      setHasSimulatedVoice(false);
      setIncomingChannel('voice');
      return;
    }
    setIncomingChannel(channel as IncomingChannel);
  };

  const handleEndAndRestartVoice = () => {
    // End current simulated voice and start a fresh incoming call
    setInteractions(prev => prev.filter(i => i.channel !== 'PHONE'));
    setHasSimulatedVoice(false);
    setIncomingChannel('voice');
  };

  const handleAcceptIncoming = () => {
    if (!incomingChannel) return;
    const data = channelSimData[incomingChannel];

    const channelMap: Record<IncomingChannel, typeof interactions[0]['channel']> = {
      voice: 'PHONE', livechat: 'LIVE CHAT', whatsapp: 'WHATSAPP',
      email: 'EMAIL', x: 'PHONE', facebook: 'PHONE', instagram: 'PHONE',
    };
    const sentimentMap: Record<IncomingChannel, SentimentType> = {
      voice: 'Neutral', livechat: 'Neutral', whatsapp: 'Frustrated',
      email: 'Concerned', x: 'Neutral', facebook: 'Neutral', instagram: 'Neutral',
    };

    const newInt = {
      id: `int-sim-${Date.now()}`,
      channel: channelMap[incomingChannel],
      citizen: data.citizen,
      topic: data.topic || data.preview.slice(0, 40),
      queue: 'MyGCC General',
      elapsed: '00:00',
      priority: 'Medium' as const,
      sentiment: sentimentMap[incomingChannel],
      status: 'Active',
    };

    setInteractions(prev => [...prev, newInt]);
    if (incomingChannel === 'voice') setHasSimulatedVoice(true);
    setIncomingChannel(null);
    toast.success(`${data.citizen} added to Active Interactions`, { description: 'Interaction workspace loading...' });
    setTimeout(() => router.push('/active-interaction-workspace'), 300);
  };

  const handleDeclineIncoming = () => {
    setIncomingChannel(null);
    toast.info('Interaction declined');
  };

  const handleReset = () => {
    setInteractions(initialInteractions);
    setRecentCases(initialCases);
    setTasks(initialTasks);
    setCompletedTasks(new Set());
    setIncomingChannel(null);
    setShowNewCase(false);
    setHasSimulatedVoice(false);
    toast.success('Demo state reset successfully', {
      description: 'All interactions, cases and AI states restored to initial configuration',
      duration: 4000,
    });
  };

  const handleCreateCase = () => {
    if (!newCaseForm.citizen || !newCaseForm.agency || !newCaseForm.topic) {
      toast.error('Please fill in required fields: Citizen, Agency, and Topic');
      return;
    }
    const newId = `MYGCC-2026-${String(Math.floor(Math.random() * 9000) + 1000).padStart(6, '0')}`;
    setRecentCases(prev => [{
      id: newId,
      citizen: newCaseForm.citizen,
      topic: newCaseForm.topic,
      agency: newCaseForm.agency,
      status: 'Open',
      priority: newCaseForm.priority as 'High' | 'Medium' | 'Low',
      sla: '8h 00m',
      slaStatus: 'ok',
    }, ...prev.slice(0, 4)]);
    setShowNewCase(false);
    setNewCaseForm({ citizen: '', agency: '', topic: '', description: '', priority: 'Medium', relatedInteraction: '', relatedTicket: '', assignment: 'Nur Aisyah' });
    toast.success(`Case ${newId} created successfully`, { description: `Assigned to ${newCaseForm.assignment}`, duration: 5000 });
  };

  return (
    <div className="relative min-h-full page-transition">
      {/* Incoming Interaction Notification */}
      {incomingChannel && (
        <IncomingInteractionNotification
          data={{ channel: incomingChannel, ...channelSimData[incomingChannel] }}
          onAccept={handleAcceptIncoming}
          onDecline={handleDeclineIncoming}
        />
      )}

      {/* New Case Modal */}
      {showNewCase && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-full max-w-[520px] max-h-[90vh] overflow-y-auto slide-in-up">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-[16px] font-700 text-foreground">New Case</h2>
                <p className="text-[12px] text-muted-foreground mt-0.5">Create a new citizen service case</p>
              </div>
              <button onClick={() => setShowNewCase(false)} className="p-2 rounded-lg hover:bg-muted transition-colors">
                <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Citizen <span className="text-red-500">*</span></label>
                  <input type="text" placeholder="Citizen name" value={newCaseForm.citizen}
                    onChange={e => setNewCaseForm(p => ({ ...p, citizen: e.target.value }))}
                    className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Agency <span className="text-red-500">*</span></label>
                  <select value={newCaseForm.agency} onChange={e => setNewCaseForm(p => ({ ...p, agency: e.target.value }))} className="input-field w-full">
                    <option value="">Select agency...</option>
                    {['MOH', 'JIM', 'JPJ', 'JPN', 'KWSP', 'LHDN', 'PERKESO', 'KPKT', 'KESUMA'].map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Topic <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Brief description of the issue" value={newCaseForm.topic}
                  onChange={e => setNewCaseForm(p => ({ ...p, topic: e.target.value }))}
                  className="input-field w-full" />
              </div>
              <div>
                <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Description</label>
                <textarea rows={3} placeholder="Detailed description..." value={newCaseForm.description}
                  onChange={e => setNewCaseForm(p => ({ ...p, description: e.target.value }))}
                  className="input-field w-full resize-none" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Priority</label>
                  <select value={newCaseForm.priority} onChange={e => setNewCaseForm(p => ({ ...p, priority: e.target.value }))} className="input-field w-full">
                    {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Assignment</label>
                  <select value={newCaseForm.assignment} onChange={e => setNewCaseForm(p => ({ ...p, assignment: e.target.value }))} className="input-field w-full">
                    {['Nur Aisyah', 'Hafizuddin Razali', 'Siti Rohaida', 'Ahmad Shahril'].map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Related Interaction</label>
                  <input type="text" placeholder="Interaction ID (optional)" value={newCaseForm.relatedInteraction}
                    onChange={e => setNewCaseForm(p => ({ ...p, relatedInteraction: e.target.value }))}
                    className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-[11px] font-700 text-muted-foreground mb-1.5">Related Ticket</label>
                  <input type="text" placeholder="Ticket ID (optional)" value={newCaseForm.relatedTicket}
                    onChange={e => setNewCaseForm(p => ({ ...p, relatedTicket: e.target.value }))}
                    className="input-field w-full" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowNewCase(false)} className="flex-1 btn-secondary py-2.5 text-[13px]">Cancel</button>
              <button onClick={handleCreateCase} className="flex-1 btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
                <Icon name="PlusIcon" size={14} />
                Create Case
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 py-3 sm:py-4">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-[16px] sm:text-[18px] font-700 text-foreground">Good afternoon, Nur Aisyah</h1>
            <p className="text-[11px] sm:text-[12px] text-muted-foreground mt-0.5">{dashboardDate || 'Loading...'} · MyGCC General Queue · Shift started 08:00</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground bg-green-50 border border-green-200 px-2.5 py-1 rounded-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              All systems operational
            </span>
            <SimulateDemoDropdown
              onSimulate={handleSimulate}
              onReset={handleReset}
              activeVoiceIsSimulated={hasSimulatedVoice}
              activeVoiceCitizen="Ahmed Firdaus"
              onEndAndRestartVoice={handleEndAndRestartVoice}
            />
            <button
              onClick={() => setShowNewCase(true)}
              className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-[12px]"
            >
              <Icon name="PlusIcon" size={13} />
              New Case
            </button>
          </div>
        </div>

        {/* Summary Stats — 2-col on mobile, 4-col on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
          {summaryStats.map((stat) => (
            <div key={`stat-${stat.label}`} className="card-elevated p-3 sm:p-4 card-hover">
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className={`p-1.5 sm:p-2 rounded-xl ${stat.bg}`}>
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={14} className={stat.color} />
                </div>
                <div className={`flex items-center gap-1 text-[10px] font-600 ${stat.positive ? 'text-green-600' : 'text-amber-600'}`}>
                  <Icon name={stat.positive ? 'ArrowTrendingUpIcon' : 'ExclamationCircleIcon'} size={10} />
                  <span className="hidden xl:inline">{stat.delta}</span>
                </div>
              </div>
              <div className="text-[20px] sm:text-[22px] font-700 font-tabular text-foreground mb-0.5">{stat.value}</div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground font-500 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Main Grid — single column on mobile, multi-column on desktop */}
        <div className="grid grid-cols-12 gap-3 sm:gap-4">
          {/* Active Interactions */}
          <div className="col-span-12 xl:col-span-5">
            <div className="card-elevated overflow-hidden">
              <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-blue-50">
                    <Icon name="InboxIcon" size={13} className="text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-700">Active Interactions</h2>
                  <span className="bg-primary text-white text-[10px] font-700 rounded-full px-1.5 py-0.5 leading-none">{interactions.length}</span>
                </div>
                <Link href="/interactions" className="text-[11px] text-primary font-600 hover:underline">View all →</Link>
              </div>
              <div className="divide-y divide-border">
                {interactions.map((item) => (
                  <Link
                    key={item.id}
                    href="/active-interaction-workspace"
                    className="flex items-start gap-3 px-3 sm:px-4 py-3 hover:bg-muted/30 transition-colors group"
                  >
                    <div className="pt-0.5 flex-shrink-0">
                      <ChannelBadge channel={item.channel} showLabel={false} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13px] font-600 text-foreground truncate">{item.citizen}</span>
                        {item.channel === 'PHONE' && item.status === 'Active' && (
                          <span className="flex items-center gap-1 text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md font-700 flex-shrink-0">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                            ACTIVE
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate mb-1.5">{item.topic}</div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <SentimentBadge sentiment={item.sentiment} showBg />
                        <PriorityBadge priority={item.priority} />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[12px] font-700 font-tabular text-muted-foreground">{item.elapsed}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[80px]">{item.queue}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Cases + Tasks + AI */}
          <div className="col-span-12 xl:col-span-7 flex flex-col gap-3 sm:gap-4">
            {/* Cases — card format on mobile, table on desktop */}
            <div className="card-elevated overflow-hidden">
              <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-50">
                    <Icon name="ClipboardDocumentListIcon" size={13} className="text-amber-600" />
                  </div>
                  <h2 className="text-[13px] font-700">My Open Cases</h2>
                </div>
                <Link href="/cases" className="text-[11px] text-primary font-600 hover:underline">View all →</Link>
              </div>

              {/* Mobile: card format */}
              <div className="sm:hidden divide-y divide-border">
                {recentCases.map((c) => (
                  <Link key={c.id} href="/cases" className="block px-3 py-3 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className="text-[11px] font-700 text-primary font-mono">{c.id}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="text-[12px] font-600 text-foreground mb-1">{c.topic}</div>
                    <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
                      <span>{c.citizen}</span>
                      <span>·</span>
                      <span>{c.agency}</span>
                      <span>·</span>
                      <PriorityBadge priority={c.priority} />
                      <span>·</span>
                      {c.slaStatus === 'breach' && <span className="sla-badge-breach">{c.sla}</span>}
                      {c.slaStatus === 'risk' && <span className="sla-badge-risk">{c.sla}</span>}
                      {c.slaStatus === 'ok' && <span className="text-muted-foreground font-tabular">{c.sla}</span>}
                      {c.slaStatus === 'resolved' && <span className="sla-badge-ok">{c.sla}</span>}
                    </div>
                  </Link>
                ))}
              </div>

              {/* Desktop: table format */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      {['Case ID', 'Citizen', 'Topic', 'Agency', 'Status', 'SLA'].map((h) => (
                        <th key={`th-${h}`} className="text-left px-3 py-2 text-[10px] font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recentCases.map((c) => (
                      <tr key={c.id} className="table-row-hover transition-colors">
                        <td className="px-3 py-2.5">
                          <Link href="/cases" className="font-700 text-primary hover:underline whitespace-nowrap text-[11px]">{c.id}</Link>
                        </td>
                        <td className="px-3 py-2.5 text-foreground whitespace-nowrap font-500">{c.citizen}</td>
                        <td className="px-3 py-2.5 text-muted-foreground max-w-[120px] truncate">{c.topic}</td>
                        <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{c.agency}</td>
                        <td className="px-3 py-2.5"><StatusBadge status={c.status} /></td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {c.slaStatus === 'breach' && <span className="sla-badge-breach">{c.sla}</span>}
                          {c.slaStatus === 'risk' && <span className="sla-badge-risk">{c.sla}</span>}
                          {c.slaStatus === 'ok' && <span className="text-[11px] text-muted-foreground font-tabular">{c.sla}</span>}
                          {c.slaStatus === 'resolved' && <span className="sla-badge-ok">{c.sla}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tasks + AI Insights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Tasks */}
              <div className="card-elevated overflow-hidden">
                <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-orange-50">
                      <Icon name="CheckCircleIcon" size={13} className="text-orange-600" />
                    </div>
                    <h2 className="text-[13px] font-700">My Tasks</h2>
                    <span className="bg-orange-500 text-white text-[10px] font-700 rounded-full px-1.5 py-0.5 leading-none">{tasks.length - completedTasks.size}</span>
                  </div>
                  <Link href="/tasks" className="text-[11px] text-primary font-600 hover:underline">All →</Link>
                </div>
                <div className="divide-y divide-border">
                  {tasks.map((task) => (
                    <div key={task.id} className={`flex items-start gap-3 px-3 sm:px-4 py-3 transition-colors ${completedTasks.has(task.id) ? 'opacity-50' : 'hover:bg-muted/20'}`}>
                      <button
                        onClick={() => setCompletedTasks(prev => {
                          const next = new Set(prev);
                          next.has(task.id) ? next.delete(task.id) : next.add(task.id);
                          if (!prev.has(task.id)) toast.success(`Task completed`);
                          return next;
                        })}
                        className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 transition-colors flex items-center justify-center ${completedTasks.has(task.id) ? 'bg-green-500 border-green-500' : task.urgent ? 'border-orange-400' : 'border-border'}`}
                      >
                        {completedTasks.has(task.id) && <Icon name="CheckIcon" size={11} className="text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[12px] font-500 leading-snug ${completedTasks.has(task.id) ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {task.label}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          {task.urgent && <span className="text-[10px] bg-orange-50 text-orange-600 border border-orange-200 px-1.5 py-0.5 rounded-md font-600">Urgent</span>}
                          <span className="text-[10px] text-muted-foreground">{task.due}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Insights */}
              <div className="ai-panel border rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 sm:px-4 py-3 border-b border-ai-border">
                  <Icon name="SparklesIcon" size={13} className="text-primary" />
                  <h2 className="text-[13px] font-700 text-primary">CX AI Insights</h2>
                  <span className="ai-badge ml-auto">AI</span>
                </div>
                <div className="divide-y divide-ai-border">
                  {aiInsights.map((insight, i) => (
                    <div key={`insight-${i}`} className="px-3 sm:px-4 py-3">
                      <div className="flex items-start gap-2 mb-2">
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${insight.type === 'critical' ? 'bg-red-500' : insight.type === 'warning' ? 'bg-amber-500' : 'bg-green-500'}`} />
                        <p className="text-[11px] text-foreground leading-snug">{insight.text}</p>
                      </div>
                      <Link
                        href={insight.href}
                        className="text-[11px] text-primary font-600 hover:underline ml-3.5"
                      >
                        {insight.action} →
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}